import { currentYYYYMM } from './overviewMonthUtils';

const pad2 = (n) => String(n).padStart(2, '0');

/** Parse DD/MM/YYYY (en-GB session date) → local Date or null. */
export function parseEnGBDateToDate(str) {
  const raw = String(str ?? '').trim();
  if (!raw || raw === '—') return null;
  const parts = raw.split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

export function dateToYYYYMMDD(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function dateToYYYYMM(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

export function formatDailyChartLabel(d) {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

function monthLabelWithYear(d) {
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function monthLabelNoYear(d) {
  return d.toLocaleDateString('en-GB', { month: 'short' });
}

function weekNumInMonth(d) {
  return Math.ceil(d.getDate() / 7);
}

export function structuredWeeklyPeriodKey(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const wn = weekNumInMonth(d);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-W${pad2(wn)}`;
}

export function weeklyPeriodKey(d) {
  return structuredWeeklyPeriodKey(d);
}

export function formatWeeklyChartLabel(d, weekNum) {
  return `${monthLabelNoYear(d)} W${weekNum}`;
}

/** Normalize API daily chart rows → { label, key, hours, sessions } sorted asc. */
export function normalizeDailyChartRows(apiRows) {
  const rows = [];
  for (const r of Array.isArray(apiRows) ? apiRows : []) {
    const d = parseEnGBDateToDate(r?.label);
    if (!d) continue;
    const key = dateToYYYYMMDD(d);
    if (!key) continue;
    rows.push({
      label: formatDailyChartLabel(d),
      key,
      hours: Number(r?.hours) || 0,
      sessions: Number(r?.sessions) || 0,
    });
  }
  return rows.sort((a, b) => a.key.localeCompare(b.key));
}

function keyToDate(key) {
  const m = String(key ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function rollupDailyToWeekly(dailyRows) {
  const buckets = new Map();
  for (const row of dailyRows) {
    const d = keyToDate(row.key);
    if (!d) continue;
    aggregateWeeklyBucket(buckets, d, row);
  }
  return [...buckets.values()].sort((a, b) => (a.sortKey || a.key).localeCompare(b.sortKey || b.key));
}

function aggregateWeeklyBucket(buckets, d, row) {
  const wn = weekNumInMonth(d);
  const key = structuredWeeklyPeriodKey(d);
  const label = formatWeeklyChartLabel(d, wn);
  const sortKey = `${dateToYYYYMM(d)}-W${pad2(wn)}`;
  const prev = buckets.get(key) || { label, key, sortKey, hours: 0, sessions: 0 };
  prev.hours = Math.round((prev.hours + (Number(row.hours) || 0)) * 10) / 10;
  prev.sessions += Number(row.sessions) || 0;
  buckets.set(key, prev);
}

export function rollupDailyToMonthly(dailyRows) {
  const buckets = new Map();
  for (const row of dailyRows) {
    const d = keyToDate(row.key);
    if (!d) continue;
    const yearMonth = dateToYYYYMM(d);
    const key = yearMonth;
    const label = monthLabelWithYear(d);
    const prev = buckets.get(key) || { label, key, yearMonth, hours: 0, sessions: 0 };
    prev.hours = Math.round((prev.hours + (Number(row.hours) || 0)) * 10) / 10;
    prev.sessions += Number(row.sessions) || 0;
    buckets.set(key, prev);
  }
  return [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
}

export function buildDisplayChartData(granularity, dailyRows) {
  if (granularity === 'weekly') return rollupDailyToWeekly(dailyRows);
  if (granularity === 'daily') return [...dailyRows];
  return rollupDailyToMonthly(dailyRows);
}

export function sessionWeeklyKey(session) {
  const d = parseEnGBDateToDate(session?.date);
  if (!d) return null;
  return structuredWeeklyPeriodKey(d);
}

export function sessionMonthlyKey(session) {
  const d = parseEnGBDateToDate(session?.date);
  if (!d) return null;
  return dateToYYYYMM(d);
}

export function sessionDailyKey(session) {
  const d = parseEnGBDateToDate(session?.date);
  if (!d) return null;
  return dateToYYYYMMDD(d);
}

export function sessionMatchesPeriod(session, granularity, periodKey) {
  if (!periodKey) return false;
  if (granularity === 'daily') return sessionDailyKey(session) === periodKey;
  if (granularity === 'weekly') return sessionWeeklyKey(session) === periodKey;
  return sessionMonthlyKey(session) === periodKey;
}

export function periodBounds(chartRows) {
  const rows = Array.isArray(chartRows) ? chartRows : [];
  const keys = rows.map((r) => r.key).filter(Boolean);
  if (!keys.length) return null;
  return { min: keys[0], max: keys[keys.length - 1], keys };
}

export function stepPeriod(chartRows, currentKey, delta) {
  const bounds = periodBounds(chartRows);
  if (!bounds?.keys.length) return currentKey;
  const idx = bounds.keys.indexOf(currentKey);
  const base = idx >= 0 ? idx : bounds.keys.length - 1;
  const next = Math.max(0, Math.min(bounds.keys.length - 1, base + delta));
  return bounds.keys[next];
}

export function canStepPeriod(chartRows, currentKey, delta) {
  const bounds = periodBounds(chartRows);
  if (!bounds?.keys.length || !currentKey) return false;
  const idx = bounds.keys.indexOf(currentKey);
  if (idx < 0) return false;
  const next = idx + delta;
  return next >= 0 && next < bounds.keys.length;
}

function todayKeys() {
  const now = new Date();
  return {
    daily: dateToYYYYMMDD(now),
    monthly: currentYYYYMM(now),
    weekly: weeklyPeriodKey(now),
  };
}

/** Default period: current calendar period if in chart, else newest bar. */
export function pickDefaultPeriodKey(granularity, chartRows) {
  const rows = Array.isArray(chartRows) ? chartRows : [];
  if (!rows.length) return '';
  const keys = new Set(rows.map((r) => r.key));
  const t = todayKeys();
  const prefer =
    granularity === 'daily' ? t.daily : granularity === 'weekly' ? t.weekly : t.monthly;
  if (prefer && keys.has(prefer)) return prefer;
  return rows[rows.length - 1].key;
}

export function formatPeriodNavigatorLabel(periodKey, granularity) {
  if (!periodKey) return '';
  if (granularity === 'monthly') {
    const [y, mo] = periodKey.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(mo)) return periodKey;
    return new Date(y, mo - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }
  if (granularity === 'weekly') {
    const m = periodKey.match(/^(\d{4})-(\d{2})-W(\d{1,2})$/i);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const wn = parseInt(m[3], 10);
      const d = new Date(y, mo, Math.min((wn - 1) * 7 + 1, new Date(y, mo + 1, 0).getDate()));
      return formatWeeklyChartLabel(d, wn);
    }
    return periodKey;
  }
  const m = periodKey.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  }
  return periodKey;
}
